import { useState } from 'react'
import { Button, ErrorBanner, Field, Modal, Select, TextInput } from '../../components/forms'
import { ApiError } from '../../lib/api'
import { formatDate, formatMoneyList } from '../../lib/format'
import { openPdf } from '../../features/messaging/api'
import { SendApprovalCode } from './SendApprovalCode'
import {
  useAcceptAccountOrder,
  useAccountOrders,
  useApprovers,
  useCancelAccountOrder,
  useCreditPosition,
  useInvoiceAccountOrder,
  useTakeAccountOrder,
  type AccountOrder,
  type CartLine,
  type PosCustomer,
  type SaleResult,
} from '../../features/pos/api'

/**
 * Taking an order on a customer's account, and the proforma that confirms it (D-199).
 *
 * **It is not a sale and the customer does not get a receipt.** Nothing has been delivered and no tax
 * point has arisen, so what they leave with is a proforma — a priced offer that says in as many words
 * that it is not a tax invoice. The tax invoice follows when the goods are released to them.
 *
 * **Nothing is set aside.** The order holds no stock, so the dialogue says so rather than letting a
 * cashier imply otherwise, and the proforma carries a date rather than a promise.
 */
export function AccountOrderButton({
  locationId,
  customer,
  cart,
  onTaken,
}: {
  locationId: string
  customer: PosCustomer
  cart: CartLine[]
  /** The basket has become an order, so the counter is free for the next person. */
  onTaken: () => void
}) {
  const credit = useCreditPosition(customer.isWalkIn ? null : customer.id)
  const take = useTakeAccountOrder(locationId)

  const [open, setOpen] = useState(false)
  const [lpo, setLpo] = useState('')
  const [forCollection, setForCollection] = useState(true)
  const [approverUserId, setApproverUserId] = useState('')
  const [pin, setPin] = useState('')
  const [reason, setReason] = useState('')
  const [taken, setTaken] = useState<AccountOrder | null>(null)

  // The approval question only appears once the server has asked it — a cashier should not be shown a
  // PIN box for a customer who is well inside their limit.
  const needsApproval = isApprovalNeeded(take.error)
  const approvers = useApprovers(locationId, needsApproval)
  const eligible = (approvers.data ?? []).filter((a) => a.canOverrideCredit)
  const chosen = eligible.find((a) => a.id === approverUserId)

  const lpoNeeded = customer.requiresPurchaseOrder === true
  const ready =
    cart.length > 0 &&
    (!lpoNeeded || lpo.trim().length > 0) &&
    (!needsApproval || (approverUserId !== '' && pin.length >= 4 && reason.trim().length > 0))

  function close() {
    setOpen(false)
    setPin('')
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    if (!ready) return

    const order = await take.mutateAsync({
      customerId: customer.id,
      lines: cart.map((l) => ({
        productId: l.productId,
        quantity: l.quantity,
        discountPercent: l.discountPercent,
        discountAmount: l.discountAmount,
        // On the order as it was sold (D-210). Sent as singles, a box line was priced as one piece
        // and the proforma asked the customer for a twelfth of what they had agreed.
        asPack: l.asPack === true,
        // And at the rate it was sold at (D-272). Dropped here, the loose pieces beside a box went
        // on the order at SHELF price — the one difference between the sale on screen and the
        // proforma the customer signs, and it only showed on baskets the till now fills routinely.
        atPackRate: l.atPackRate === true,
      })),
      customerOrderNumber: lpo.trim() || undefined,
      forCollection,
      approverUserId: needsApproval ? approverUserId : undefined,
      pin: needsApproval ? pin : undefined,
      approvalReason: needsApproval ? reason.trim() : undefined,
    })

    setPin('')
    setLpo('')
    setReason('')
    setApproverUserId('')
    setTaken(order)
    onTaken()
  }

  if (customer.isWalkIn) {
    // Said rather than hidden: a cashier who cannot find the button assumes the feature is missing.
    return (
      <p className="text-xs text-dpl-muted">
        An order on account needs a named customer — take their details above first.
      </p>
    )
  }

  return (
    <>
      <Button tone="secondary" disabled={cart.length === 0} onClick={() => setOpen(true)}>
        Order on account
      </Button>

      {credit.data && (
        <span className="self-center text-xs text-dpl-muted">
          {formatMoneyList(credit.data.available)} of credit left
          {credit.data.committed > 0 && ` · ${formatMoneyList(credit.data.committed)} on ${credit.data.openOrders} open order${credit.data.openOrders === 1 ? '' : 's'}`}
        </span>
      )}

      <Modal open={open} title={`Order on ${customer.name}'s account`} onClose={close}>
        <form onSubmit={submit} className="space-y-4 text-sm">
          {/* A 409 is the approval question, not a failure: its fields are below, so showing it as a
              red banner as well would read as two problems. */}
          <ErrorBanner error={needsApproval ? null : take.error} />

          {needsApproval && take.error instanceof ApiError && (
            <p className="rounded-md bg-dpl-accent/10 px-3 py-2 text-[#6B4700]">{take.error.detail ?? take.error.title}</p>
          )}

          {credit.data && (
            <dl className="grid grid-cols-[1fr_auto] gap-x-4 gap-y-1 rounded-lg bg-dpl-bg px-3 py-2">
              <dt>Credit limit</dt>
              <dd className="text-right tabular-nums">{formatMoneyList(credit.data.creditLimit)}</dd>
              <dt>Owing on invoices</dt>
              <dd className="text-right tabular-nums">{formatMoneyList(credit.data.outstanding)}</dd>
              {/* Its own line, never folded into the balance: these are goods they have ordered and
                  not received, and a customer will rightly dispute a figure that mixes the two. */}
              <dt>On open orders</dt>
              <dd className="text-right tabular-nums">{formatMoneyList(credit.data.committed)}</dd>
              <dt className="font-semibold">Left to spend</dt>
              <dd className="text-right font-semibold tabular-nums">{formatMoneyList(credit.data.available)}</dd>
            </dl>
          )}

          <Field
            label={lpoNeeded ? 'Their LPO number' : 'Their LPO number (optional)'}
            htmlFor="order-lpo"
            hint={lpoNeeded ? `${customer.name} will not pay an invoice without it.` : undefined}
          >
            <TextInput id="order-lpo" autoFocus value={lpo} onChange={(e) => setLpo(e.target.value)} />
          </Field>

          <Field label="How they will get it" htmlFor="order-collection">
            <Select
              id="order-collection"
              value={forCollection ? 'collect' : 'deliver'}
              onChange={(e) => setForCollection(e.target.value === 'collect')}
            >
              <option value="collect">Collecting from this shop</option>
              <option value="deliver">To be delivered</option>
            </Select>
          </Field>

          {needsApproval && (
            <>
              <Field label="Approved by" htmlFor="order-approver" hint="The credit check did not pass on its own.">
                <Select id="order-approver" value={approverUserId} onChange={(e) => setApproverUserId(e.target.value)}>
                  <option value="">Choose…</option>
                  {eligible.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name} — {a.role}
                    </option>
                  ))}
                </Select>
              </Field>

              <SendApprovalCode locationId={locationId} approver={chosen} purpose="CreditOverride" />

              <Field
                label="Their code"
                htmlFor="order-pin"
                hint="The six digits from their phone — or their PIN, if they have set one."
              >
                <TextInput
                  id="order-pin"
                  type="password"
                  inputMode="numeric"
                  autoComplete="off"
                  maxLength={8}
                  value={pin}
                  onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
                />
              </Field>

              <Field label="Reason" htmlFor="order-reason" hint="Recorded on the order and printed on the proforma.">
                <TextInput id="order-reason" value={reason} onChange={(e) => setReason(e.target.value)} />
              </Field>
            </>
          )}

          <p className="rounded-md border border-dpl-border bg-dpl-bg px-3 py-2">
            Nothing is set aside yet. They get a proforma — not a tax invoice — and we hold the goods
            once they confirm it.
          </p>

          <Button type="submit" busy={take.isPending} disabled={!ready}>
            Take the order
          </Button>
        </form>
      </Modal>

      <Modal open={taken !== null} title="Order taken" onClose={() => setTaken(null)}>
        {taken && (
          <div className="space-y-3 text-sm">
            <p>
              <strong>{taken.orderNumber}</strong> for <strong>{formatMoneyList(taken.total)}</strong> on{' '}
              {taken.customerName}'s account.
            </p>

            <p className="rounded-md border border-dpl-border bg-dpl-bg px-3 py-2">
              The proforma is on its way to them by email. It holds the prices until{' '}
              {formatDate(taken.validUntil)} and sets nothing aside — tell them we put the goods by when
              they confirm.
            </p>

            <div className="flex flex-wrap gap-2">
              <Button tone="secondary" onClick={() => void openPdf({ kind: 'proforma', id: taken.id })}>
                Print the proforma
              </Button>
              <Button tone="ghost" onClick={() => setTaken(null)}>
                Done
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </>
  )
}

/**
 * Whether the server answered "this needs an approval" rather than refusing outright.
 *
 * The API answers 409 with the needs in the problem's extensions, exactly as it does for a discount
 * over a cashier's limit — so the same shape is read here rather than matching on the words of a
 * message, which would break the first time somebody reworded one.
 */
function isApprovalNeeded(error: unknown): boolean {
  return error instanceof ApiError && error.status === 409 && Array.isArray(error.extensions.approvals)
}

/**
 * The orders this customer already has open, and what a counter does with them (D-200).
 *
 * **Three things happen to an order and they are deliberately separate.** *Confirm* is the customer
 * saying yes — the first moment anybody has promised the goods, so it is the first moment stock comes
 * off the shelf, for three working days. *Invoice* is the goods going out: the tax point, and with it
 * the real invoice, the eTIMS receipt and the debtor entry. *Cancel* puts back whatever was held.
 *
 * A till that collapsed confirm and invoice into one button would either freeze shelves for orders
 * nobody confirmed, or hand over goods with no invoice behind them.
 */
export function AccountOrdersButton({
  locationId,
  registerSessionId,
  customer,
  onInvoiced,
}: {
  locationId: string
  registerSessionId: string
  customer: PosCustomer
  /** The sale exists now, so the till can show its receipt. */
  onInvoiced: (sale: SaleResult) => void
}) {
  const orders = useAccountOrders(locationId, customer.isWalkIn ? null : customer.id)
  const accept = useAcceptAccountOrder(locationId)
  const invoice = useInvoiceAccountOrder(locationId)
  const cancel = useCancelAccountOrder(locationId)

  const [open, setOpen] = useState(false)
  const waiting = orders.data ?? []

  if (customer.isWalkIn || waiting.length === 0) {
    return null
  }

  return (
    <>
      <Button tone="ghost" onClick={() => setOpen(true)}>
        Their orders ({waiting.length})
      </Button>

      <Modal open={open} title={`${customer.name}'s open orders`} onClose={() => setOpen(false)} wide>
        <ErrorBanner error={accept.error ?? invoice.error ?? cancel.error} />

        <ul className="space-y-2">
          {waiting.map((order) => (
            <li key={order.id} className="rounded-lg border border-dpl-border p-3 text-sm">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <span className="min-w-0">
                  <span className="block font-medium">
                    {order.orderNumber} · {formatMoneyList(order.total)}
                  </span>
                  <span className="block text-xs text-dpl-muted">
                    {order.status === 'Submitted' ? 'Proforma sent, not confirmed' : 'Confirmed, goods held'}
                    {' · '}{order.lines.length} line{order.lines.length === 1 ? '' : 's'}
                    {order.lpo && ` · LPO ${order.lpo}`}
                    {order.status === 'Submitted' && ` · prices hold to ${formatDate(order.validUntil)}`}
                  </span>

                  {/* Said before anybody presses: an order whose goods have gone since can still be
                      invoiced, but not for more than is there. */}
                  {order.status === 'Submitted' && order.lines.some((l) => l.available < l.quantity) && (
                    <span className="mt-1 block text-xs font-medium text-dpl-warning">
                      The shop is short of some of this now — nothing was set aside.
                    </span>
                  )}
                </span>

                <span className="flex shrink-0 flex-wrap gap-2">
                  {order.status === 'Submitted' && (
                    <Button
                      tone="secondary"
                      busy={accept.isPending}
                      onClick={() => accept.mutate({ id: order.id })}
                    >
                      Confirm &amp; hold
                    </Button>
                  )}

                  <Button
                    busy={invoice.isPending}
                    onClick={async () => {
                      const sale = await invoice.mutateAsync({ id: order.id, registerSessionId })
                      onInvoiced(sale)
                      setOpen(false)
                    }}
                  >
                    Invoice &amp; release
                  </Button>

                  <Button tone="ghost" onClick={() => void openPdf({ kind: 'proforma', id: order.id })}>
                    Proforma
                  </Button>

                  {/* Asked for, and kept: an order that vanished with no reason is a customer
                      telephoning about goods nobody can account for. */}
                  <Button
                    tone="ghost"
                    busy={cancel.isPending}
                    onClick={() => {
                      const reason = window.prompt(`Why is ${order.orderNumber} being cancelled?`)
                      if (reason?.trim()) cancel.mutate({ id: order.id, reason: reason.trim() })
                    }}
                  >
                    Cancel
                  </Button>
                </span>
              </div>
            </li>
          ))}
        </ul>
      </Modal>
    </>
  )
}
